import { FunctionComponent, useCallback, useState } from "react";

import { Grid } from "@mui/material";
import { usePathname, useSearchParams } from "next/navigation";
import { useRouter } from "next/router";

import { useMediaQuery } from "../../hooks/useMediaQuery";

import { EmailEditorHeader } from "./Components/EmailEditorHeader";
import { EmailEditor, EmailLoader } from "./EmailEditor";
import { useEmailPreview } from "./hooks/useEmailPreview";
import { useGetEmailContent } from "./useGetEmailContent";

import { AmplitudeEvents } from "@/constants/amplitude";
import { DashboardDrawer } from "@/dashboard-design-system/Drawer";
import { trpc } from "@/helpers/trpc";
import { useAmplitude } from "@/hooks/amplitude/useAmplitude";

export type EmailEditorDrawerProps = {
  emailContentId?: string;
  closeEmailEditor: () => void;
  isOpen: boolean;
  onEmailGenerated?: (transactionId: string, body: string) => void;
};

export const EmailEditorDrawer: FunctionComponent<React.PropsWithChildren<EmailEditorDrawerProps>> = (props) => {
  const router = useRouter();
  const pathname = usePathname();
  const { logAmplitudeEvent } = useAmplitude();
  const { isSmallScreen } = useMediaQuery();

  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());
  const thankYouTransactionId = searchParams.get("thankYouTransactionId");

  const { mutateAsync: deleteDraftEmailContentMutation } = trpc.automaticDeleteDraftEmailContent.useMutation();

  const {
    emailContent,
    isLoading: emailContentIsLoading,
    refetchEmailContent,
  } = useGetEmailContent(props.emailContentId);
  const isLoading = !emailContent || emailContentIsLoading;

  const [recipientsCount, setRecipientsCount] = useState(0);
  const { previewOpened, openPreview, closePreview } = useEmailPreview({ emailContent });
  const [openSaveChangesDialog, setOpenSaveChangesDialog] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);

  const handleCloseDrawer = useCallback(
    ({ confirmUnsaved = false }) =>
      async () => {
        let draftDeleted = false;

        if (isFormDirty && confirmUnsaved) {
          setOpenSaveChangesDialog(true);
          return;
        } else {
          if (thankYouTransactionId) {
            setOpenSaveChangesDialog(false);
          }

          logAmplitudeEvent(AmplitudeEvents.EmailEditorClickClose, { openFrom: emailContent?.openedFrom });

          // Delete the draft thank you email content from database

          if (thankYouTransactionId && emailContent?.id) {
            try {
              await deleteDraftEmailContentMutation({ emailContentId: emailContent.id });
              draftDeleted = true;
            } catch {
              // Silently fail - don't block drawer from closing if delete fails
            }
          }
        }

        // If we deleted the draft, no need to refetch as it won't exist anymore
        if (!draftDeleted) {
          refetchEmailContent();
        }
        props.closeEmailEditor();

        const deliveryId = searchParams.get("deliveryId");
        if (deliveryId) {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { deliveryId, from, showTour, ...params } = query;
          router.push(`${pathname}?${new URLSearchParams(params as Record<string, string>).toString()}`);
        }
      },
    [
      isFormDirty,
      thankYouTransactionId,
      props,
      searchParams,
      logAmplitudeEvent,
      emailContent?.openedFrom,
      emailContent?.id,
      deleteDraftEmailContentMutation,
      refetchEmailContent,
      query,
      router,
      pathname,
    ]
  );

  const closeEmailEditor = useCallback(() => {
    setOpenSaveChangesDialog(false);
    props.closeEmailEditor();
  }, [props]);

  const refetch = useCallback(async () => {
    await refetchEmailContent();
  }, [refetchEmailContent]);

  return (
    <>
      <DashboardDrawer
        open={props.isOpen}
        onClose={handleCloseDrawer({ confirmUnsaved: !thankYouTransactionId })}
        slots={{ header: <></> }}
        width={previewOpened ? "90%" : undefined}
        noPadding
        disableEnforceFocus
      >
        <div style={{ height: "100%" }} data-test="email-editor-drawer">
          <Grid container sx={{ height: "100%" }} paddingTop={2} paddingBottom={2} direction="column">
            <Grid item marginBottom={isSmallScreen ? 3 : 5} paddingLeft={2} paddingRight={2}>
              <EmailEditorHeader
                handleClose={handleCloseDrawer({ confirmUnsaved: !thankYouTransactionId })}
                recipientsCount={recipientsCount}
                previewOpened={previewOpened}
                openPreview={openPreview}
                closePreview={closePreview}
                emailType={emailContent?.emailType}
                hidePreviewButton={Boolean(thankYouTransactionId)}
              />
            </Grid>
            <Grid item xs style={{ maxWidth: "100%", overflowY: "auto" }}>
              {isLoading || (!emailContent && <EmailLoader />)}
              {!isLoading && emailContent && (
                <EmailEditor
                  emailContent={emailContent}
                  onClose={closeEmailEditor}
                  refetch={refetch}
                  setIsFormDirty={setIsFormDirty}
                  openSaveChangesDialog={openSaveChangesDialog}
                  setOpenSaveChangesDialog={setOpenSaveChangesDialog}
                  previewOpened={previewOpened}
                  openPreview={openPreview}
                  closePreview={closePreview}
                  setRecipientsCount={setRecipientsCount}
                  onEmailGenerated={
                    thankYouTransactionId
                      ? (body) => {
                          props.onEmailGenerated?.(thankYouTransactionId, body);
                        }
                      : undefined
                  }
                />
              )}
            </Grid>
          </Grid>
        </div>
      </DashboardDrawer>
    </>
  );
};
